import { Form, Input } from 'antd';
import { FormAttachment, FormSelect, FormTextArea, FormNumber, FormTitle } from '../form/FormBasic';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import RESOURCE_CONFIGS from '../../constants/resourceConfig';
import { FileTypeEnum } from '../../enums/FileTypeEnum';
import FormSelectCustomTwoRowPerOption from '../form/FormSelectCustomTwoRowPerOption';
import { useForm, useWatch } from 'antd/es/form/Form';
import PrimaryButton from '../buttons/PrimaryButton';
import DefaultButton from '../buttons/DefaultButton';
import { REGEX_ALL_ALLOWED_SAFE_FILTER } from '../../constants/regex';
import {
  maxLengthRule,
  minLengthRule,
  noSpacesOnlyRule,
  regexRule,
  requiredRule,
} from '../../helpers/formHelper';
import AddProductForm from '../product/AddProduct';
import { Plus, XSquare } from 'react-bootstrap-icons';
import FormModelComponent from '../model/FormModelComponent';
import { usePresentationFormOptions } from '../../hooks/usePresentationFormOptions';
import { PresentationStatusEnum } from '../../enums/PresentationStatusEnum';
import { PriceAddPresentationFormProps } from '../../types/priceTypes';
import { usePermissions } from '../../hooks/usePermissions';
import { RoleName } from '../../enums/RoleNameEnm';
import { PermissionEnum } from '../../enums/PermissionEnum';

const CitizenPriceAddPresentationForm: React.FC<PriceAddPresentationFormProps> = ({
  form,
  addPresentationToList,
  resetPresentationSection = 0,
  presentationFromTask,
  onShowSelectedSectionChange,
}) => {
  const { t } = useTranslation(['price', 'form', 'product']);
  const [addProductForm] = useForm();

  const {
    mainProductOptions,
    enrichedProductsOptions,
    selectedProduct,
    showSelectedProductSection,
    isProductModalOpen,
    hasCustomFields,
    onSearch,
    handleNewProductButton,
    closeNewProductModal,
    handleCancel,
    handleNewProductAddition,
    autoSelectedNewProduct,
    loadingMainProducts,
    loadingPresentations,
  } = usePresentationFormOptions({
    form,
    productFromTask: presentationFromTask,
    status: PresentationStatusEnum.PENDING,
    resetProductSection: resetPresentationSection,
    addProductForm,
    onShowSelectedSectionChange,
  });

  const priceVal = useWatch('price', form);
  const photoOrInvoiceVal = useWatch('photoOrInvoice', form);
  const productRemarksVal = useWatch('productRemarks', form);
  const photoOrInvoiceConvertedValue = useWatch(`photoOrInvoiceConverted`, form);
  const [hasErrors, setHasErrors] = useState(false);
  const { hasPermission, hasRoles } = usePermissions();
  const isMobile = useMediaQuery({
    query: `(max-width: ${RESOURCE_CONFIGS.RESPONSIVE_BREAKPOINTS.SM})`,
  });

  const hasAllFieldsValue =
    !!priceVal &&
    photoOrInvoiceVal?.length > 0 &&
    (!productRemarksVal || productRemarksVal?.length < 256);

  return (
    <>
      <FormModelComponent
        isOpen={isProductModalOpen}
        header={t('product:createProductTitle')}
        subheader={t('product:createProductSubTitle')}
        content={
          <AddProductForm
            form={addProductForm}
            handleCancel={handleCancel}
            getConfirmation={handleNewProductAddition}
            isModal={true}
            isEdit={false}
          />
        }
        onClickedCancel={closeNewProductModal}
        footerAction={{
          confimation: {
            label: t('product:createProduct'),
            onClick: handleNewProductAddition,
          },
          cancel: {
            label: t('cancel'),
            onClick: handleCancel,
          },
        }}
        closeIcon={<XSquare className="text-primary-font-red" size={20} />}
        contentStyles="mb-4 px-2 max-h-[60vh] md:max-h-[70vh] overflow-y-auto space-y-2"
      />

      <div className="mb-4">
        <FormTitle
          label={t('addPresentationHeading')}
          styles="text-lg font-bold text-black md:text-xl mb-4"
        />
      </div>

      <Form
        form={form}
        name="addProductDataForm"
        layout="vertical"
        onFinish={addPresentationToList}
        onFieldsChange={(_, allFields) => {
          const errors = allFields.some((field) => field.errors && field.errors.length > 0);
          setHasErrors(errors);
        }}
        initialValues={{ wasInSuggestedPresentation: 'yes', wasUnderSuggestedBrand: 'yes' }}
      >
        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end [&_.ant-form-item]:!mb-0">
          <div className="w-full md:flex-1">
            {/* Main Product */}
            <FormSelect
              name="mainProductName"
              options={mainProductOptions}
              label={t('product')}
              placeholder={t('form:dropdownPlaceholder', { field: t('product') })}
              isRequired={false}
              allowClear={true}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
              }
              loading={loadingMainProducts}
            />
          </div>

          <div className="w-full md:flex-1">
            <Form.Item name="productSearch" label={t('presentationFinder')}>
              <FormSelectCustomTwoRowPerOption
                placeHolder={t('presentationFindFieldPlaceholder')}
                options={enrichedProductsOptions}
                onSearchSelected={onSearch}
                autoSelectedLabel={autoSelectedNewProduct}
                loading={loadingPresentations}
              />
            </Form.Item>
          </div>

          {!showSelectedProductSection &&
            hasRoles([RoleName.CITIZEN]) &&
            hasPermission(PermissionEnum.CREATE_PRESENTATION) && (
              <div className="w-full md:w-auto">
                <DefaultButton
                  handleOnClick={handleNewProductButton}
                  type="button"
                  width="100%"
                  height="40px"
                  className="flex items-center justify-center px-4"
                >
                  <div className="flex items-center justify-center gap-2">
                    <Plus className="h-5 w-5" />
                    <span className="whitespace-nowrap text-sm leading-none md:text-base">
                      {t('product:newProduct')}
                    </span>
                  </div>
                </DefaultButton>
              </div>
            )}
        </div>

        {showSelectedProductSection && (
          <div className={'rounded-lg border border-gray-200 p-4'}>
            <h2 className="text-md font-bold">{t('product:presentationDetails')}</h2>
            <div className="my-4 grid grid-cols-1 gap-4 rounded-lg bg-gray-100 p-4 md:grid-cols-4">
              <div>
                <p className="font-bold">{t('nameLabel')}</p>
                <p>{selectedProduct[0]?.name}</p>
              </div>
              <div>
                <p className="font-bold">{t('unitLabel')}</p>
                <p>{selectedProduct[0]?.unit}</p>
              </div>
              <div>
                <p className="font-bold">{t('presentationLabel')}</p>
                <p>{selectedProduct[0]?.presentation || 'N/A'}</p>
              </div>
              <div>
                <p className="font-bold">{t('brandLabel')}</p>
                <p>{selectedProduct[0]?.brand || 'N/A'}</p>
              </div>

              {hasCustomFields && selectedProduct[0]?.customFields
                ? selectedProduct[0].customFieldsMetadata.map(
                    (field: { value: any; fieldName: string }, idx: number) => {
                      return (
                        <div key={idx}>
                          <p className="font-bold">{field.fieldName}:</p>
                          <p>{field.value ?? t('notGiven')}</p>
                        </div>
                      );
                    }
                  )
                : null}
            </div>

            <div>
              <FormNumber
                name="price"
                label={t('price')}
                placeholder={t('priceFieldPlaceholder')}
                isRequired={true}
                rules={[
                  {
                    required: true,
                    message: t('priceFieldRequired'),
                  },
                ]}
                min="0.01"
                max="99999999"
                type="float"
                precision={2}
              />

              <FormAttachment
                name="photoOrInvoice"
                label={t('productPhotoOrInvoice')}
                isRequired={true}
                maxCount={5}
                form={form}
                fileTypes={[FileTypeEnum.PNG, FileTypeEnum.JPEG, FileTypeEnum.JPG]}
                validationMessage={t('invoicePhotoRequired')}
                initialFiles={photoOrInvoiceConvertedValue ? photoOrInvoiceConvertedValue : []}
                cameraOnly={isMobile}
              />

              <FormTextArea
                name="productRemarks"
                label={t('productRemarks')}
                placeholder={t('RespondentObservationsFieldPlaceholder')}
                disabled={false}
                isRequired={false}
                rules={[
                  regexRule(t, REGEX_ALL_ALLOWED_SAFE_FILTER),
                  noSpacesOnlyRule(t, t('productRemarks')),
                  minLengthRule(t, t('productRemarks'), 3),
                  maxLengthRule(t, t('productRemarks'), 200),
                ]}
              />
            </div>
            <Form.Item name="productName" label="Product Name" hidden>
              <Input hidden />
            </Form.Item>
            <Form.Item name="productRefId" label="Product Ref Id" hidden>
              <Input hidden />
            </Form.Item>
            <Form.Item name="photoOrInvoiceConverted" label="photoOrInvoiceConverted Value" hidden>
              <Input hidden />
            </Form.Item>

            <PrimaryButton
              handleOnClick={addPresentationToList}
              disabled={!hasAllFieldsValue || hasErrors}
              width="100%"
              height="40px"
              hideBgGradient={true}
              className="mt-2 flex items-center justify-center"
            >
              {t('addPresentationToList')}
            </PrimaryButton>
          </div>
        )}
      </Form>
    </>
  );
};

export default CitizenPriceAddPresentationForm;
